import { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
// @mui
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
// config
import { HEADER, NAVBAR } from '../../config';
// Redux
import { getDialog } from '../../redux/slices/dialog'
import { useSelector, useDispatch } from '../../redux/store';
// components
import AdminDashboardHeader from './header/index';
import AdminNavbar from './navbar';
import { SectionLoading } from '../../components/SectionLoading';
import { DialogController } from '../../components/dialog';
// ----------------------------------------------------------------------

const MainStyle = styled('main', {
  shouldForwardProp: (prop) => prop !== 'collapseClick',
})(({ collapseClick, theme }) => ({
  flexGrow: 1,
  paddingTop: HEADER.MOBILE_HEIGHT + 24,
  paddingBottom: HEADER.MOBILE_HEIGHT + 24,
  [theme.breakpoints.up('lg')]: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
    paddingBottom: HEADER.DASHBOARD_DESKTOP_HEIGHT + 24,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)`,
    transition: theme.transitions.create('margin-left', {
      duration: theme.transitions.duration.shorter,
    }),
    ...(collapseClick && {
      marginLeft: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
    }),
  },
}));

// ----------------------------------------------------------------------

export default function AdminLayout() {
  const dispatch = useDispatch();
  const { collapseClick, isCollapse } = useCollapseDrawer();
  const { dialogStatus, dialogType, dialogData } = useSelector((state) => state.dialog);
  const [open, setOpen] = useState(false);

  // Add inactivity dialog state and timer
  const [inactivityDialog, setInactivityDialog] = useState(false);
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
  // const INACTIVITY_TIMEOUT = 10 * 1000; // 10 seconds
  let inactivityTimer;

  // Reset inactivity timer on user activity
  const resetInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    inactivityTimer = setTimeout(() => {
      setInactivityDialog(true);
    }, INACTIVITY_TIMEOUT);
  };

  // Handle page reload
  const handleReload = () => {
    window.location.reload();
  };

  // Setup activity listeners
  useEffect(() => {
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'mousemove'];
    
    activityEvents.forEach(event => {
      window.addEventListener(event, resetInactivityTimer);
    });

    // Initial timer setup
    resetInactivityTimer();

    // Cleanup
    return () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      activityEvents.forEach(event => {
        window.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, []);

  const dialogClose = () => {
    dispatch(getDialog({ dialogStatus: !dialogStatus, dialogType:'', dialogData: '' }));
  };

  return (
    <Box
      sx={{
        display: { lg: 'flex' },
        minHeight: { lg: 1 },
      }}
    >
      <SectionLoading />

      <AdminDashboardHeader isCollapse={isCollapse} onOpenSidebar={() => setOpen(true)} />

      <AdminNavbar isOpenSidebar={open} onCloseSidebar={() => setOpen(false)} />

      <MainStyle collapseClick={collapseClick}>
        <Outlet />
      </MainStyle>
      
      {/* Dispatched Modal */}
      <DialogController 
        open={dialogStatus} 
        onClose={dialogClose} 
        dialogType={dialogType}
        dialogData={dialogData}
      />

      {/* Inactivity Dialog */}
      <Dialog
        open={inactivityDialog}
        onClose={() => {}} // Prevent closing on backdrop click
        disableEscapeKeyDown // Prevent closing on Escape key
      >
        <DialogTitle>Session Inactive</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your admin session has been inactive. Please reload the page to ensure everything is up to date.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReload} variant="contained" color="primary" fullWidth>
            Reload Page
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
